<template>
  <div class="login-page-wrapper">
    <AuthCard
        class="login-page"
        :title="t(currentStep === 'email' ? 'page.login-title' : 'page.verify-title')"
        :subtitle="t(currentStep === 'email' ? 'page.login-subtitle' : '')"
    >
      <template #header>
        <div class="login-page__logo">
          <img :src="logo" alt="logo" />
          <img :src="assistant" alt="assistant" />
        </div>

        <a-button
            class="login-page__back-button"
            type="link"
            v-if="isCurrentStepVerify"
            @click="handleBackToEmail"
        >
          <LeftOutlined /> {{ t('ui.back-button-label') }}
        </a-button>
      </template>

      <LoginEmailForm
          v-if="currentStep === 'email'"
          :loading="loading"
          :has-error="hasError"
          @submit="handleEmailSubmit"
          @sign-up="handleSignUp"
      />

      <LoginVerifyForm
          v-else-if="currentStep === 'verify'"
          :email="form.email"
          :initial-code="form.code"
          :loading="loading"
          :has-error="hasError"
          @submit="handleVerifySubmit"
          @back="handleBackToEmail"
          @resend="handleResendCode"
      />

      <template #after-form>
        <TextView v-if="currentStep === 'email'" variant="roboto_15_semibold">
          {{ t('ui.empty-user-account-first-part') }}
          <a-typography-link
              href="/sign-up"
              :style="{ fontSize: 'inherit', fontWeight: 'inherit', fontFamily: 'inherit' }"
          >
            {{ t('ui.empty-user-account-second-part') }}
          </a-typography-link>
        </TextView>
      </template>
    </AuthCard>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { globalNotification } from '@/composables/use-app-notification'
import { useRoute } from 'vue-router'
import { AuthCard } from '@/ui/common/auth'
import LoginEmailForm from './components/LoginEmailForm.vue'
import LoginVerifyForm from './components/LoginVerifyForm.vue'
import TextView from '@/ui/common/text-view/TextView.vue'
import { useAuthStore } from '@/store/auth-store'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { router } from '@/config/router/router'
import { Routes } from '@/config/router/routes'
import { LeftOutlined } from '@ant-design/icons-vue'

import logo from '@/assets/icons/menu/logo.svg'
import assistant from '@/assets/icons/menu/assistant.svg'

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const { loading } = storeToRefs(authStore)

const currentStep = ref<'email' | 'verify'>('email')
const hasError = ref<boolean>(false)

const form = reactive({
  email: '',
  code: '',
})

const isCurrentStepVerify = computed(() => currentStep.value === 'verify')

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const isValidCode = (code: string): boolean => {
  return /^\d{6}$/.test(code)
}

onMounted(async () => {
  const emailParam = route.query.email as string | undefined
  const codeParam = route.query.code as string | undefined

  if (!emailParam || !codeParam) {
    return
  }

  try {
    const decodedEmail = decodeURIComponent(emailParam)

    if (!isValidEmail(decodedEmail)) {
      globalNotification.error(t('error.invalid-email-format'))
      return
    }

    if (!isValidCode(codeParam)) {
      globalNotification.error(t('error.invalid-code-format'))
      return
    }

    form.email = decodedEmail.trim()
    form.code = codeParam.trim()
    currentStep.value = 'verify'

    // Автоматическая верификация, если код уже есть
    if (form.code.length === 6) {
      await handleVerifySubmit(form.code)
    }
  } catch (error) {
    hasError.value = true
    console.error('Auto-verification failed:', error)
  }
})

const handleEmailSubmit = async (email: string) => {
  if (!isValidEmail(email)) {
    hasError.value = true
    return
  }

  form.email = email.trim()
  hasError.value = false

  try {
    await authStore.getCode(form.email)
    currentStep.value = 'verify'
  } catch (error) {
    hasError.value = true
  }
}

const handleVerifySubmit = async (code: string) => {
  if (!isValidCode(code)) {
    hasError.value = true
    return
  }

  form.code = code.trim()
  hasError.value = false

  try {
    await authStore.verifyCode(form.email, form.code)
  } catch (error) {
    hasError.value = true
  }
}

const handleBackToEmail = () => {
  currentStep.value = 'email'
  hasError.value = false
  form.code = ''
}

const handleResendCode = async () => {
  try {
    await authStore.getCode(form.email)
  } catch (error) {
    console.error(error)
  }
}

const handleSignUp = () => {
  router.push(Routes.SignUp)
}
</script>

<style lang="scss" scoped>
.login-page-wrapper {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;

  .login-page {
    &__logo {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      padding: 16px 24px;
      gap: 8px;
    }

    &__back-button {
      margin-right: auto;
      padding: 8px;
    }
  }
}
</style>